import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  X,
  Receipt,
  CreditCard,
  Banknote,
  Building,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Loader2
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';

// ────────────────────────────────────────────────────────────────────
// STEP DEFINITIONS
// ────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: 'Invoice Info', icon: Receipt, hint: 'Basic invoice details' },
  { id: 1, label: 'Client', icon: User, hint: 'Client information and billing' },
  { id: 2, label: 'Project', icon: Target, hint: 'Project details' },
  { id: 3, label: 'Items', icon: FileText, hint: 'Line items and pricing' },
  { id: 4, label: 'Summary', icon: DollarSign, hint: 'Financial summary' },
  { id: 5, label: 'Additional', icon: FileText, hint: 'Notes and terms' },
];

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    // Invoice Information
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'draft',
    
    // Client Information
    client: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    
    // Project Information
    project: '',
    description: '',
    
    // Billing Details
    items: [],
    
    // Financial Information
    subtotal: 0,
    taxRate: 0,
    taxAmount: 0,
    total: 0,
    
    // Additional Information
    notes: '',
    terms: 'Payment due within 30 days of invoice date.',
    paymentMethods: ['Bank Transfer', 'Check', 'Credit Card']
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsResponse, customersResponse] = await Promise.all([
          fetch(API_CONFIG.ENDPOINTS.PROJECTS, { headers: API_CONFIG.HEADERS }),
          fetch(API_CONFIG.ENDPOINTS.CUSTOMERS, { headers: API_CONFIG.HEADERS })
        ]);

        if (projectsResponse.ok) {
          const data = await projectsResponse.json();
          
          const projectsArray =
          Array.isArray(data?.data?.projects)
          ? data.data.projects
          : Array.isArray(data?.projects)
          ? data.projects
          : [];
          
          setProjects(projectsArray);
        }

        if (customersResponse.ok) {
          const data = await customersResponse.json();
          const customers = data.data || [];
          setClients(customers.map((customer) => ({
            id: customer._id || customer.id,
            name: customer.name,
            email: customer.primaryContact?.email || '',
            phone: customer.primaryContact?.phone || '',
            address: customer.addresses?.billing?.street || customer.addresses?.headquarters?.street || ''
          })));
        }
      } catch (err) {
        showError('Failed to load projects or clients');
      }
    };

    loadData();
    
    // Set default due date (30 days from today)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    setFormData(prev => ({
      ...prev,
      dueDate: dueDate.toISOString().split('T')[0]
    }));
    
    // Generate invoice number
    const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
    setFormData(prev => ({
      ...prev,
      invoiceNumber
    }));
  }, []);

  // Initialize with default item
  useEffect(() => {
    if (formData.items.length === 0) {
      setFormData(prev => ({
        ...prev,
        items: [{
          id: 1,
          description: '',
          quantity: 1,
          rate: 75,
          amount: 75
        }]
      }));
    }
  }, []);

  // Calculate totals when items change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      total
    }));
  }, [formData.items, formData.taxRate]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle client selection
  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        client: clientId,
        clientName: selectedClient.name,
        clientEmail: selectedClient.email,
        clientPhone: selectedClient.phone,
        clientAddress: selectedClient.address
      }));
    }
  };

  // Handle item changes
  const handleItemChange = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  // Add new item
  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 75,
      amount: 75
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Remove item
  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  // Navigation functions
  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (idx) => {
    if (idx <= currentStep) setCurrentStep(idx);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const selectedClient = clients.find(c => c.id === formData.client);
      const selectedProject = Array.isArray(projects)
  ? projects.find(p => (p._id || p.id) === formData.project)
  : null;

      const payload = {
        ...formData,
        clientName: selectedClient?.name || '',
        clientEmail: selectedClient?.email || '',
        clientPhone: selectedClient?.phone || '',
        clientAddress: selectedClient?.address || '',
        project: selectedProject?._id || selectedProject?.id,
        projectName: selectedProject?.name || ''
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECT_INVOICES, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create invoice');
      }

      success('Invoice created successfully!');
      navigate('/projects/time-tracking/billing');
    } catch (err) {
      console.error('Error creating invoice:', err);
      showError(err.message || 'Failed to create invoice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Step Content Renderers ───────────────────────────────────

  // STEP 0: Invoice Information
  const renderStep0 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Invoice Information
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Basic information about the invoice
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Invoice Number *
          </label>
          <input
            type="text"
            className="form-control"
            value={formData.invoiceNumber}
            onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
            required
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Invoice Date *
          </label>
          <input
            type="date"
            className="form-control"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            required
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Due Date *
          </label>
          <input
            type="date"
            className="form-control"
            value={formData.dueDate}
            onChange={(e) => handleInputChange('dueDate', e.target.value)}
            required
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Status
          </label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
    </div>
  );

  // STEP 1: Client Information
  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Client Information
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Details about the client and billing address
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Client *
          </label>
          <select
            className="form-select"
            value={formData.client}
            onChange={(e) => handleClientChange(e.target.value)}
            required
          >
            <option value="">Select Client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Client Name
          </label>
          <input
            type="text"
            className="form-control"
            value={formData.clientName}
            onChange={(e) => handleInputChange('clientName', e.target.value)}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Email
          </label>
          <input
            type="email"
            className="form-control"
            value={formData.clientEmail}
            onChange={(e) => handleInputChange('clientEmail', e.target.value)}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Phone
          </label>
          <input
            type="text"
            className="form-control"
            value={formData.clientPhone}
            onChange={(e) => handleInputChange('clientPhone', e.target.value)}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
      </div>
      
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
          Client Address
        </label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.clientAddress}
          onChange={(e) => handleInputChange('clientAddress', e.target.value)}
          readOnly
          style={{ backgroundColor: '#f8f9fa' }}
        />
      </div>
    </div>
  );

  // STEP 2: Project Information
  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Project Information
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Project details and description
        </p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Project *
          </label>
          <select
            className="form-select"
            value={formData.project}
            onChange={(e) => handleInputChange('project', e.target.value)}
            required
          >
            <option value="">Select Project</option>
            {Array.isArray(projects) &&
            projects.map(project => (
            <option key={project._id || project.id} value={project._id || project.id}>
              {project.name}
            </option>
          ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Description
          </label>
          <input
            type="text"
            className="form-control"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief description of the work performed"
          />
        </div>
      </div>
    </div>
  );

  // STEP 3: Invoice Items
  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Invoice Items
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Line items for the invoice
        </p>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {formData.items.map((item, index) => (
          <div key={item.id} style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
            gap: '12px',
            alignItems: 'flex-start',
            padding: '16px',
            border: '1px solid var(--border-color)',
            borderRadius: '0',
            position: 'relative',
            marginBottom: '12px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>Description *</label>
              <input
                type="text"
                className="form-control"
                value={item.description}
                onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                placeholder="Service description"
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>Quantity</label>
              <input
                type="number"
                className="form-control"
                value={item.quantity}
                onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value))}
                min="0"
                step="0.25"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>Rate ($)</label>
              <input
                type="number"
                className="form-control"
                value={item.rate}
                onChange={(e) => handleItemChange(item.id, 'rate', parseFloat(e.target.value))}
                min="0"
                step="0.01"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>Amount ($)</label>
              <input
                type="number"
                className="form-control"
                value={item.amount}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            
            <button
              type="button"
              onClick={() => handleRemoveItem(item.id)}
              disabled={formData.items.length === 1}
              style={{
                marginTop: '28px',
                padding: '8px',
                color: formData.items.length === 1 ? 'var(--text-muted)' : 'var(--text-muted)',
                background: 'none',
                border: 'none',
                cursor: formData.items.length === 1 ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => { if (formData.items.length > 1) e.target.style.color = '#dc2626'; }}
              onMouseLeave={(e) => { if (formData.items.length > 1) e.target.style.color = 'var(--text-muted)'; }}
            >
              <X size={20} />
            </button>
          </div>
        ))}
      </div>
      
      <button
        type="button"
        onClick={handleAddItem}
        style={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--gold-primary)',
          fontSize: '14px',
          fontWeight: '600',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 0'
        }}
      >
        <Plus size={18} /> Add Another Item
      </button>
    </div>
  );

  // STEP 4: Financial Summary
  const renderStep4 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Financial Summary
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Invoice totals and tax information
        </p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Subtotal ($)
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.subtotal.toFixed(2)}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Tax Rate (%)
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.taxRate}
            onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value))}
            min="0"
            max="100"
            step="0.01"
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Tax Amount ($)
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.taxAmount.toFixed(2)}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Total ($)
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.total.toFixed(2)}
            readOnly
            style={{ backgroundColor: '#f8f9fa', fontWeight: '600', fontSize: '16px' }}
          />
        </div>
      </div>
    </div>
  );

  // STEP 5: Additional Information
  const renderStep5 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Additional Information
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Notes, terms, and payment information
        </p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Notes
          </label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Additional notes or comments..."
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: 'var(--text-primary)' }}>
            Payment Terms
          </label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.terms}
            onChange={(e) => handleInputChange('terms', e.target.value)}
            placeholder="Payment terms and conditions..."
          />
        </div>
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create Invoice"
        description="Create and manage invoices for time tracking and project billing"
      />
      <div style={{ padding: '24px' }}>
        <div style={{
          background: 'white',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden'
        }}>
          {/* Multi-step Header */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div style={{ padding: '32px' }}>
            <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}>
              {/* Animated Current Step */}
              <div key={currentStep} style={{ animation: 'fadeIn 0.25s ease-in' }}>
                {stepRenderers[currentStep]()}
              </div>

              {/* Navigation Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '32px 0 0 0',
                borderTop: '1px solid var(--border-color)',
                marginTop: '40px'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => navigate('/projects/time-tracking/billing')}
                    className="btn btn-secondary"
                    style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="btn btn-secondary"
                      style={{
                        minWidth: '100px',
                        borderRadius: '0',
                        padding: '12px 24px',
                        border: '1px solid var(--gold-primary)',
                        color: 'var(--gold-primary)'
                      }}
                    >
                      <ChevronLeft size={16} />
                      Back
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {/* Step dots */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {STEPS.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx === currentStep ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: idx < currentStep ? 'var(--gold-bright)' : idx === currentStep ? 'var(--gold-primary)' : 'var(--divider-color)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </div>

                  {currentStep < STEPS.length - 1 ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{
                        minWidth: '130px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary"
                      style={{
                        minWidth: '160px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: isSubmitting ? 'var(--text-muted)' : 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          Create Invoice
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Global Fade In Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CreateInvoice;
